"use client";

import React, { useEffect, useRef, useState } from "react";
import { CalendarHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ScheduleATour() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="scheduleatour" className="bg-background relative overflow-hidden py-24 px-6 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40">
        <div className="bg-secondary/20 h-[40rem] w-[40rem] rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`relative mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <h2 className="font-serif text-foreground mb-6 text-4xl font-medium tracking-tight sm:text-5xl">
          Schedule a Tour of Our Assisted Living in Silver Spring, Maryland
        </h2>
        
        <p className="text-muted-foreground mx-auto mb-10 max-w-2xl text-lg leading-relaxed sm:text-xl">
          Experience the warmth of Family First Senior Care firsthand. Visit our small assisted living home with personalized care to see our family-like environment and meet our dedicated 24/7 caregivers. Discover how our residential assisted living in Montgomery County provides a safe, nurturing space for up to 5 seniors. We look forward to welcoming you.
        </p>

        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20 h-14 rounded-full px-8 text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <Link to="/ScheduleATourPage">
              <CalendarHeart className="mr-2 h-5 w-5" />
              Schedule a Tour
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}